import type { Vec2, Vec3 } from '@fafi/shared';

// Aim models (§13.2). Mouse = absolute (world-hit) aim; stick = relative aim. Both feed the same
// pass/shot code. Pure math — the device→ray/stick sampling wraps these (E2E-tested).

/**
 * Intersect a ray with the pitch plane (three world y = 0) and return the sim-space (x, y) hit
 * (three.x → sim.x, three.z → sim.y). Null if the ray is parallel to or points away from the plane.
 */
export function pitchPlaneRayHit(origin: Vec3, dir: Vec3): Vec2 | null {
  if (Math.abs(dir.y) < 1e-12) return null;
  const t = -origin.y / dir.y;
  if (t < 0) return null;
  return { x: origin.x + dir.x * t, y: origin.z + dir.z * t };
}

/** Absolute aim: yaw from the player to a world target (§13.2 / T-CLI-002). */
export function worldAimYaw(from: Vec2, to: Vec2): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

/**
 * Relative aim from the right stick (§13.2 / T-CLI-003): aimYaw = cameraYaw + stickAngle. Inside the
 * deadzone the aim is left unchanged (returns prevAim).
 */
export function gamepadRelativeAim(
  stickX: number,
  stickY: number,
  cameraYaw: number,
  prevAim: number,
  deadzone: number,
): number {
  if (Math.hypot(stickX, stickY) < deadzone) return prevAim;
  return cameraYaw + Math.atan2(stickY, stickX);
}

/** Left-stick movement → camera-relative world moveVec (deadzone → zero). Mirrors the keyboard mapping. */
export function gamepadMove(stickX: number, stickY: number, cameraYaw: number, deadzone: number): Vec2 {
  if (Math.hypot(stickX, stickY) < deadzone) return { x: 0, y: 0 };
  const fwd = -stickY; // stick up (negative Y) = forward
  const right = stickX;
  const cos = Math.cos(cameraYaw);
  const sin = Math.sin(cameraYaw);
  let x = fwd * cos - right * sin;
  let y = fwd * sin + right * cos;
  const m = Math.hypot(x, y);
  if (m > 1) {
    x /= m;
    y /= m;
  }
  return { x, y };
}
